import { UNITS } from './data/units.data.js';
import type { Unit, UnitQuantity, UnitReferenceRow } from './types.js';

type UnitEntry = (typeof UNITS)[number];

/** Every unit key — `'KILOGRAM' | 'METRE' | …`. */
export type UnitValue = UnitEntry['value'];

/** The keys of one quantity — `UnitOf<'weight'>` is `'GRAM' | 'KILOGRAM' | …`. */
export type UnitOf<Q extends UnitQuantity> = Extract<
  UnitEntry,
  { quantity: Q }
>['value'];

/** `{ KILOGRAM: 'KILOGRAM', … }` — the key-mirror shape the APIs index into. */
export type UnitEnum<V extends string> = { readonly [K in V]: K };

/** Every unit key, in list order. A tuple, so it can feed `z.enum`. */
export const UNIT_VALUES = Object.freeze(UNITS.map(u => u.value)) as readonly [
  UnitValue,
  ...UnitValue[],
];

const BY_VALUE: ReadonlyMap<string, Unit> = new Map(
  UNITS.map(u => [u.value, u]),
);

export const toUnitEnum = <V extends string>(
  values: readonly V[],
): UnitEnum<V> =>
  Object.freeze(Object.fromEntries(values.map(v => [v, v]))) as UnitEnum<V>;

export const UNIT_ENUM: UnitEnum<UnitValue> = toUnitEnum(UNIT_VALUES);

/** The keys of one quantity, in list order. */
export const unitValuesOf = <Q extends UnitQuantity>(
  quantity: Q,
): UnitOf<Q>[] =>
  UNITS.filter(u => u.quantity === quantity).map(u => u.value as UnitOf<Q>);

export const findUnit = (value: string | null | undefined): Unit | undefined =>
  value ? BY_VALUE.get(value) : undefined;

export const isUnitValue = (value: unknown): value is UnitValue =>
  typeof value === 'string' && BY_VALUE.has(value);

/**
 * Every unit as `{ id, name }`, both the key — the rows the unit-of-measure
 * table used to return for data-point validation and exports.
 */
export const listUnitReferenceRows = (): UnitReferenceRow[] =>
  UNIT_VALUES.map(v => ({ id: v, name: v }));

/**
 * Units a BOM line accepts when its component's style type configures no
 * quantity units — what the batch and style endpoints validate against, and
 * what both frontends offer, in that case.
 */
export const DEFAULT_QUANTITY_UNIT_VALUES = Object.freeze([
  'PIECES',
  'PAIR',
  'PERCENTAGE',
  'ROLL',
  'DENIER',
  'JUTE',
  'ENGLISH_COUNT',
  'METRIC_COUNT',
  'WORSTED_COUNT',
  'TEX',
  'GRAM',
  'KILOGRAM',
  'LITRE',
  'METRIC_TON',
  'MILLIMETRE',
  'CENTIMETRE',
  'METRE',
  'SQUARE_CENTIMETRE',
  'SQUARE_METRE',
] as const satisfies readonly UnitValue[]);

/**
 * The units the legacy public API (v0 and v1) accepts: everything except the
 * gram-per-area units added after it was frozen. Deprecated and published, so
 * its contract does not grow with the list.
 */
const NOT_IN_LEGACY_PUBLIC_API: ReadonlySet<string> = new Set([
  'GRAM_PER_SQUARE_FOOT',
  'GRAM_PER_SQUARE_YARD',
]);

export const LEGACY_PUBLIC_API_UNIT_VALUES = Object.freeze(
  UNIT_VALUES.filter(v => !NOT_IN_LEGACY_PUBLIC_API.has(v)),
) as readonly UnitValue[];
